import { gql } from "@gygadmin/client3";

export const IndividualPricingCategoryFields = gql`
  fragment IndividualPricingCategoryFields on IndividualPricingCategory {
    id
    ageRange {
      maxAge
      minAge
    }
    bookIndependently
    bookingType
    commissionRate
    ticketCategory
  }
`;

export const GroupPricingCategoryFields = gql`
  fragment GroupPricingCategoryFields on GroupPricingCategory {
    id
    commissionRate
    isAdditionalPaxForGroup
  }
`;

export const AvailabilityBlockFields = gql`
  fragment AvailabilityBlockFields on AvailabilityBlock {
    id
    name
    type
    activityId
    activityOptionId
    activityOptionPricingId
    validityDateRange {
      start
      end
    }
    specialDates {
      date
      timeSlots {
        from
        to
      }
    }
    weekdays {
      day
      timeSlots {
        from
        to
      }
    }
  }
`;

export const AddonFields = gql`
  fragment AddonFields on Addon {
    id
    pricingId
    commissionRate
    resolvedCommissionRate
    pricingScales {
      id
      maxParticipants
      minParticipants
      prices {
        breakagePrice
        id
        netRatePrice
        retailPrice
        returnPrice
      }
    }
    text {
      id
      addonId
      description
      languageId
    }
  }
`;

export const PricingFields = gql`
  ${IndividualPricingCategoryFields}
  ${GroupPricingCategoryFields}
  ${AddonFields}
  fragment PricingFields on ActivityOptionPricing {
    id
    name
    participantsRange {
      max
      min
    }
    groupVacanciesPerTimeSlot
    maxIndividualParticipantsPerTimeSlot
    pricesPerCategory {
      id
      ... on IndividualPricingCategory {
        ...IndividualPricingCategoryFields
      }
      ... on GroupPricingCategory {
        ...GroupPricingCategoryFields
      }
      pricingScales {
        id
        maxParticipants
        minParticipants
        prices {
          breakagePrice
          id
          netRatePrice
          retailPrice
          returnPrice
        }
      }
    }
    addons {
      ...AddonFields
    }
  }
`;

export const PricingConfigurationFields = gql`
  ${IndividualPricingCategoryFields}
  ${GroupPricingCategoryFields}
  ${AvailabilityBlockFields}
  ${AddonFields}
  ${PricingFields}
  fragment PricingConfigurationFields on ActivityOptionPricing {
    ...PricingFields
    pricingToAvailabilityBlocks {
      id
      availabilityBlock {
        ...AvailabilityBlockFields
      }
    }
  }
`;

export const PricingConfigurationsByActivityOption = gql`
  ${PricingConfigurationFields}
  ${AvailabilityBlockFields}
  query PricingConfigurationsByActivityOption($activityOptionId: ID!) {
    activityOption(id: $activityOptionId) {
      id
      activity {
        id
        supplier {
          id
          isGygSupplier
          preferences {
            currencyId
          }
        }
      }
      inventoryConfiguration {
        usesGroupPricing
        usesPricingOverApi
        minParticipantsOnlyForFirstBooking
      }
      pricingConfigurations {
        ...PricingConfigurationFields
      }
      inactiveAvailabilityBlocks {
        ...AvailabilityBlockFields
      }
    }
  }
`;

export const DeletePricingById = gql`
  mutation DeletePricingById($id: ID!) {
    deletePricing(id: $id) {
      deleted
      message
    }
  }
`;

export const UpdatePricing = gql`
  ${PricingFields}
  mutation UpdatePricing($input: PricingInput!, $pricingId: ID!) {
    updatePricing(input: $input, pricingId: $pricingId) {
      success
      message
      activityOptionPricing {
        ...PricingFields
      }
    }
  }
`;

export const CreatePricing = gql`
  ${PricingConfigurationFields}
  mutation CreatePricing($input: PricingInput!) {
    createPricing(input: $input) {
      success
      message
      activityOptionPricing {
        ...PricingConfigurationFields
      }
    }
  }
`;

export const ConnectAvailability = gql`
  mutation connectAvailabilityToPricing($input: AvailabilityToPricingInput!) {
    connectAvailabilityToPricing(input: $input) {
      success
      message
      pricingToAvailabilityBlock {
        id
      }
    }
  }
`;

export const DisconnectAvailability = gql`
  mutation disconnectAvailabilityToPricing($input: AvailabilityToPricingInput!) {
    disconnectAvailabilityToPricing(input: $input) {
      success
      message
    }
  }
`;
